export interface CoachInvitation {
  id: string;
  recipientEmail: string;
  lastAttemptAt: string;
  claimedAt: string | null;
  claimedByUserId: string | null;
  // Missing on older servers; accepted history must fail closed in that case.
  canRemoveAccepted?: boolean;
  previouslyAccepted?: boolean;
}

export interface CoachInvitationPage {
  invitations: CoachInvitation[];
  nextOffset: number | null;
}

export interface InvitationLeader {
  id: string;
  email: string;
}

export function parseCoachInvitationPage(value: unknown): CoachInvitationPage {
  if (!value || typeof value !== "object") throw new Error("Invitation history is unavailable.");
  const page = value as Record<string, unknown>;
  if (!Array.isArray(page.invitations) || !(page.nextOffset === null || (Number.isSafeInteger(page.nextOffset) && Number(page.nextOffset) > 0))) {
    throw new Error("Invitation history is unavailable.");
  }
  for (const row of page.invitations) {
    if (!row || typeof row.id !== "string" || typeof row.recipientEmail !== "string" || !row.recipientEmail.trim()
      || typeof row.lastAttemptAt !== "string" || !Number.isFinite(Date.parse(row.lastAttemptAt))
      || !(row.claimedAt === null || typeof row.claimedAt === "string")
      || !(row.canRemoveAccepted === undefined || typeof row.canRemoveAccepted === "boolean")
      || !(row.previouslyAccepted === undefined || typeof row.previouslyAccepted === "boolean")
      || !(row.claimedByUserId === null || typeof row.claimedByUserId === "string")) {
      throw new Error("Invitation history is unavailable.");
    }
  }
  return page as unknown as CoachInvitationPage;
}

export function buildUnlinkedCoachInvitations(invitations: CoachInvitation[], leaders: InvitationLeader[]) {
  const linkedIds = new Set(leaders.map(leader => leader.id));
  const linkedEmails = new Set(leaders.map(leader => leader.email.trim().toLowerCase()));
  const recipients = new Map<string, { latest: CoachInvitation; previouslyAccepted: boolean; canRemoveAccepted: boolean; claimedIds: Set<string> }>();
  for (const invitation of invitations) {
    const email = invitation.recipientEmail.trim().toLowerCase();
    const previous = recipients.get(email);
    const previouslyAccepted = Boolean(invitation.previouslyAccepted || invitation.claimedAt || invitation.claimedByUserId || previous?.previouslyAccepted);
    const latest = previous && Date.parse(previous.latest.lastAttemptAt) > Date.parse(invitation.lastAttemptAt) ? previous.latest : invitation;
    const claimedIds = previous?.claimedIds ?? new Set<string>();
    if (invitation.claimedByUserId) claimedIds.add(invitation.claimedByUserId);
    const canRemoveAccepted = invitation.canRemoveAccepted === true && (!previous || previous.canRemoveAccepted);
    recipients.set(email, { latest: { ...latest, recipientEmail: email }, previouslyAccepted, canRemoveAccepted, claimedIds });
  }
  return [...recipients.values()].filter(invitation => {
    if (invitation.claimedIds.size) return ![...invitation.claimedIds].some(id => linkedIds.has(id));
    return !linkedEmails.has(invitation.latest.recipientEmail);
  }).map(({ latest, previouslyAccepted, canRemoveAccepted }) => ({ ...latest, previouslyAccepted, canRemoveAccepted }))
    .sort((a, b) => Number(a.previouslyAccepted) - Number(b.previouslyAccepted)
    || Date.parse(b.lastAttemptAt) - Date.parse(a.lastAttemptAt)
    || a.recipientEmail.localeCompare(b.recipientEmail));
}
